import { offerImageSx } from 'components/offers/offerImages';
import { refreshOfferDefaultImage } from '../../../../useOfferImagePreview';
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, CircularProgress, FormControl, FormHelperText, Tab, Tabs, Typography } from '@mui/material';
import { CloudUploadOutlined } from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { request, parseResponse } from 'utils/api';
import { FileUploader } from 'react-drag-drop-files';
import { getUploadErrorMessage, MAX_SOURCE_IMAGE_BYTES, SOURCE_IMAGE_SIZE_MESSAGE, IMAGE_SIZE_MESSAGE, MAX_IMAGE_BYTES, parseImageUploadResponse } from 'components/image/uploadErrors';
import SuggestedImages from './SuggestedImages';
import { getSuggestedImageErrorMessage, isStockPhotoBusy } from './suggestedImageErrors';
import { isServicePaused, STOCK_PHOTOS_BUSY } from 'utils/serviceAvailability';
import OfferImageLibrary, { resolveOfferImageUrl } from './OfferImageLibrary';
import ImageCropDialog from '../../../../../../components/BusinessForm/components/ImageCropDialog';
import { CHATGPT_IMAGE_VALIDATION_ENABLED } from 'constants/imageValidation';

export { getUploadErrorMessage } from 'components/image/uploadErrors';

const MAX_FILE_SIZE = MAX_SOURCE_IMAGE_BYTES;
const SUPPORTED_IMAGE_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const ImageUpload = ({
  control,
  businessId,
  keywords = [],
  headline1 = '',
  headline2 = '',
  description = '',
  error,
  name = 'offerImage',
  placeholder = 'Cover image',
  uploadUrl,
  helperText,
  cropDialogZIndex,
  disabled = false,
}) => {
  //console.log(" Component Rendered: ImageUpload");

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const downloadController = useRef(null);
  useEffect(() => () => downloadController.current?.abort(), []);
  const [source, setSource] = useState(businessId ? 'history' : 'upload');
  const [libraryRevision, setLibraryRevision] = useState(0);
  const inputRef = useRef(null);
  const onChangeRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showMaxSizeText, setShowMaxSizeText] = useState(true);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const processFile = (file, callback) => {
    if (!file || disabledRef.current) return;

    setShowMaxSizeText(false);

    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(SOURCE_IMAGE_SIZE_MESSAGE, { variant: 'error' });
      setUploadStatus({ message: 'Not accepted', color: 'red' });
      return;
    }

    const fileType = file.type;
    const fileName = file.name.toLowerCase();
    const isImageByType = fileType && SUPPORTED_IMAGE_FORMATS.includes(fileType);
    const isImageByExtension =
      fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png');

    if (!isImageByType && !isImageByExtension) {
      console.error(" Unsupported file type:", fileType);
      enqueueSnackbar('Choose a JPG or PNG image.', { variant: 'error' });
      setUploadStatus({ message: 'Not accepted', color: 'red' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (mounted.current && reader.result) {
        onChangeRef.current = callback;
        setImageToCrop(reader.result);
        setCropDialogOpen(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSuggestedImage = async (photo, callback) => {
    if (disabledRef.current || isServicePaused(STOCK_PHOTOS_BUSY)) return;
    const controller = new AbortController();
    downloadController.current?.abort();
    downloadController.current = controller;
    setIsUploading(true);
    try {
      const blob = await request(`/api/offer/${businessId}/images/pexels/${photo.id}/content`, {
        headers: { Accept: 'image/jpeg,image/png' },
        credentials: 'include',
        signal: controller.signal,
      }, response => {
        if (!response.ok) {
          return parseResponse(response);
        }
        return response.blob();
      });
      if (!mounted.current || controller.signal.aborted) return;
      const extension = blob.type === 'image/png' ? 'png' : 'jpg';
      processFile(new File([blob], `pexels-${photo.id}.${extension}`, { type: blob.type }), callback);
    } catch (error) {
      if (mounted.current && !controller.signal.aborted && !error.sessionHandled) {
        if (isStockPhotoBusy(error)) throw error;
        enqueueSnackbar(getSuggestedImageErrorMessage(error, 'Couldn’t load this image. Please try again.'), { variant: 'error' });
      }
    } finally {
      if (mounted.current && !controller.signal.aborted) setIsUploading(false);
    }
  };

  const handleImageSelect = (e, callback) => {
    const files = e.target.files;
    if (!files.length) return;
    processFile(files[0], callback);
  };

  const handleFileChange = (file, callback) => {
    if (file) {
      processFile(file, callback);
    }
  };

  const handleCropComplete = (croppedFile) => {
    if (disabledRef.current) return;
    setCropDialogOpen(false);
    setImageToCrop(null);
    if (croppedFile && onChangeRef.current) {
      validateImage(croppedFile, onChangeRef.current);
      onChangeRef.current = null;
    }
  };

  const validateImage = (img, callback) => {
    if (disabledRef.current) return;
    if (img.size > MAX_IMAGE_BYTES) {
      enqueueSnackbar(IMAGE_SIZE_MESSAGE, { variant: 'error' });
      setUploadStatus({ message: 'Not accepted', color: 'red' });
      return;
    }
    if (!CHATGPT_IMAGE_VALIDATION_ENABLED) {
      uploadLogo(img, callback);
      return;
    }

    //console.log("🔍 Validating image:", img.name);

    const formData = new FormData();
    formData.append('file', img);
    setIsUploading(true);

    const validationUrl = 'https://www.hiddenunderground.com/imagevalidation/analyze-imagee/';
    // const validationUrl = 'https://www.test.hiddenunderground.com/imagevalidation/analyze-imagee/';

    //console.log("📡 Sending validation request to:", validationUrl);

    fetch(validationUrl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then((response) => {
        //console.log(" Validation API Response:", response);

        if (response && response.answer) {
          const answer = response.answer.trim().toLowerCase();
          //console.log("Validation answer:", answer);

          if (answer.startsWith('no')) {
            uploadLogo(img, callback);
          } else {
            console.warn("Image rejected by validation API.");
            enqueueSnackbar('This image didn’t pass our content review. Choose another image.', { variant: 'error' });
            setUploadStatus({ message: 'Not accepted', color: 'red' });
            setIsUploading(false);
          }
        } else {
          console.error("Invalid response from validation service.");
          enqueueSnackbar("We couldn't validate your image. Please try again in a few minutes.", { variant: 'error' });
          setUploadStatus({ message: 'Not accepted', color: 'red' });
          setIsUploading(false);
        }
      })
      .catch((err) => {
        console.error("Validation failed:", err);
        enqueueSnackbar("We couldn’t validate your image. Please try again in a few minutes.", { variant: 'error' });
          setUploadStatus({ message: 'Not accepted', color: 'red' });
        setIsUploading(false);
      });
  };

  const uploadLogo = (img, callback) => {
    if (disabledRef.current) { setIsUploading(false); return; }
    //console.log("Uploading image:", img.name);

    const formData = new FormData();
    formData.append('file', img);
    setIsUploading(true);

    request(uploadUrl, {
      method: 'POST',
      body: formData,
      mode: 'cors',
      withCredentials: true,
      credentials: 'include',
    }, parseImageUploadResponse)
      .then((response) => {
        if (!mounted.current) return;
        //console.log("Upload successful. Response:", response);

        enqueueSnackbar('Image uploaded', { variant: 'success' });

        const url = resolveOfferImageUrl(response.result);

        //console.log("Image URL:", url);
        callback(url);
        setLibraryRevision((revision) => revision + 1);
        refreshOfferDefaultImage(businessId);

        setUploadStatus({ message: 'Accepted', color: 'green' });
      })
      .catch((err) => {
        if (err.sessionHandled) return;
        console.error("Upload failed:", err);
        enqueueSnackbar(getUploadErrorMessage(err), { variant: 'error' });
        setUploadStatus({ message: 'Not accepted', color: 'red' });
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleClick = () => {
    //console.log("📁 File input clicked");
    if (!inputRef || !inputRef.current) {
      //console.error("Input ref is null");
      return;
    }
    inputRef.current.click();
  };

  return (
    <>
      <FormControl fullWidth variant="outlined" margin="dense">
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, pt: 2 }}>
                  <Typography fontWeight={500}>{placeholder}</Typography>
                  {field.value && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.5 }}>
                    <Box component="img" src={resolveOfferImageUrl(field.value)} alt="Selected offer image"
                      sx={{ ...offerImageSx, width: 80, borderRadius: 1 }} />
                    <Typography variant="body2" color="text.secondary">Current selection</Typography>
                  </Box>}
                </Box>
                <Tabs value={source} onChange={(_, next) => setSource(next)} aria-label="Image source"
                  variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ '& .MuiTab-root': { whiteSpace: 'nowrap', minWidth: 'max-content', flexGrow: 1, px: 1.5 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                  {businessId && <Tab value="history" label="Your images" id={`${name}-history-tab`} aria-controls={`${name}-history-panel`} />}
                  <Tab value="upload" label="Upload image" id={`${name}-upload-tab`} aria-controls={`${name}-upload-panel`} />
                  {businessId && <Tab value="suggested" label="Stock photos" id={`${name}-suggested-tab`} aria-controls={`${name}-suggested-panel`} />}
                </Tabs>
                <Box role="tabpanel" id={`${name}-upload-panel`} aria-labelledby={`${name}-upload-tab`} hidden={source !== 'upload'} sx={{ p: 2 }}>
                  <FileUploader
                    handleChange={(file) => handleFileChange(file, field.onChange)}
                    name="file"
                    types={['PNG', 'JPG', 'JPEG']}
                    disabled={disabled || isUploading}
                    maxSize={MAX_SOURCE_IMAGE_BYTES / (1024 * 1024)}
                    onSizeError={() => enqueueSnackbar(SOURCE_IMAGE_SIZE_MESSAGE, { variant: 'error' })}
                    onTypeError={() => enqueueSnackbar('Choose a JPG or PNG image.', { variant: 'error' })}
                  >
                    <Box sx={{ textAlign: 'center', py: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Button onClick={(event) => { event.preventDefault(); handleClick(); }} disabled={disabled || isUploading}
                        aria-label="Choose offer image file" startIcon={isUploading ? <CircularProgress size={18} /> : <CloudUploadOutlined />}>
                        {isUploading ? 'Uploading…' : 'Choose a file'}
                      </Button>
                      <Typography variant="body2" color="text.secondary">Or drag and drop it here</Typography>
                      <Typography variant="caption" color="text.secondary">JPG or PNG, up to 2 MB</Typography>
                    </Box>
                  </FileUploader>
                </Box>
                {businessId && <Box role="tabpanel" id={`${name}-history-panel`} aria-labelledby={`${name}-history-tab`} hidden={source !== 'history'} sx={{ p: 2 }}>
                  <OfferImageLibrary
                    key={`${businessId}:${libraryRevision}`}
                    businessId={businessId}
                    value={field.value}
                    dialogZIndex={cropDialogZIndex}
                    onChange={field.onChange}
                    disabled={disabled || isUploading}
                  />
                </Box>}
                {businessId && <Box role="tabpanel" id={`${name}-suggested-panel`} aria-labelledby={`${name}-suggested-tab`} hidden={source !== 'suggested'} sx={{ p: 2 }}>
                  <SuggestedImages businessId={businessId} keywords={keywords}
                    headline1={headline1} headline2={headline2} description={description} active={source === 'suggested'}
                    disabled={disabled || isUploading || cropDialogOpen} onSelect={(photo) => handleSuggestedImage(photo, field.onChange)} />
                </Box>}
              </Box>
              <input
                accept=".png,.jpg,.jpeg"
                onChange={(e) => handleImageSelect(e, field.onChange)}
                ref={inputRef}
                type="file"
                style={{ display: 'none' }}
              />
              <ImageCropDialog
                disabled={disabled}
                offerMode
                defaultAspect={16 / 9}
                optimizeForOffer
                open={cropDialogOpen}
                imageSrc={imageToCrop}
                onClose={() => {
                  setCropDialogOpen(false);
                  setImageToCrop(null);
                  onChangeRef.current = null;
                }}
                onCropComplete={handleCropComplete}
                zIndex={cropDialogZIndex}
              />
            </>
          )}
        />
        {error && <FormHelperText error>{error.message}</FormHelperText>}
      </FormControl>

      {/* Show max size text only if uploadStatus is not set */}
      {showMaxSizeText && helperText && (
          <Typography variant="caption" color="textSecondary">
            {helperText}
          </Typography>
        )}
      {uploadStatus && (
        <Typography style={{ color: uploadStatus.color, marginTop: '8px' }}>
          {uploadStatus.message}
        </Typography>
      )}
    </>
  );
};

export default ImageUpload;
